import { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { FileDropZone, FileList } from "../../../../ui";
import { FileIcon } from "../../../../images";
import { CustomButton } from "../../../../ui/CustomButton";

type Props = {
  isLoading: boolean;
  maxFiles?: number;
  setFiles: (file: File[]) => void;
};

export const UploadFromDesktopMethod = ({
  isLoading,
  maxFiles = 1,
  setFiles,
}: Props) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  useEffect(() => {
    setFiles(uploadedFiles);
  }, [uploadedFiles]);

  const onFileUpload = (files: File[]) => {
    setUploadedFiles([...uploadedFiles, ...files]);
  };

  const changeFileHandler = (fileName: string, file: File) => {
    if (isLoading) return;
    setUploadedFiles((state) =>
      state.map((el) => {
        if (el.name !== fileName) return el;
        return file;
      }),
    );
  };

  const removeFileHandler = (fileName: string) => {
    if (isLoading) return;
    setUploadedFiles((state) => state.filter(({ name }) => name !== fileName));
  };

  if (maxFiles === 1) {
    return uploadedFiles.length === 0 ? (
      <FileDropZone
        maxFiles={maxFiles}
        isDisabled={isLoading}
        uploadedFiles={uploadedFiles}
        onFileUpload={onFileUpload}
        acceptedExtensions={["csv", "xls", "xlsx"]}
      />
    ) : (
      <Box
        sx={{
          border: "2px dashed #DEE0E2",
          borderRadius: "8px",
          padding: "26px",
          textAlign: "center",
          backgroundColor: "#FCFCFC",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        <FileIcon />
        <Typography variant="body1" color="textSecondary" fontWeight={500}>
          {uploadedFiles[0].name}
        </Typography>
        <CustomButton
          onClick={() => setUploadedFiles([])}
          variant="text"
          type="button"
          sx={{
            gap: 0,
            textWrap: "nowrap",
            color: "#1C3C6C",
            backgroundColor: "transparent",
            "&:hover": {
              color: "#1C3C6C",
              backgroundColor: "#F1F1F1",
            },
            height: "32px",
          }}
          customSize="fit"
          disabled={isLoading}
        >
          Remove
        </CustomButton>
      </Box>
    );
  }

  return (
    <Stack gap="16px">
      <FileDropZone
        maxFiles={maxFiles}
        isDisabled={isLoading}
        uploadedFiles={uploadedFiles}
        onFileUpload={onFileUpload}
        acceptedExtensions={["csv", "xls", "xlsx"]}
        label={"Drag your CSV or Excel file here"}
      />
      {maxFiles > 1 && (
        <FileList
          files={uploadedFiles}
          isDisabled={isLoading}
          removeFileHandler={removeFileHandler}
          changeFileHandler={changeFileHandler}
          acceptedExtensions={["csv", "xls", "xlsx"]}
        />
      )}
    </Stack>
  );
};
